import { type ReactNode, type RefObject, useEffect, useId, useRef } from "react";
import styles from "./CmsConfirmDialog.module.css";

/**
 * `danger` is for a change that destroys work and cannot be undone — the confirm button goes red and so
 * does the detail panel. `warning` is for a change that needs confirming but takes nothing away: amber
 * detail, neutral confirm button. Reserving red for the destructive case is what makes it mean something.
 */
type CmsConfirmTone = "warning" | "danger";

export type CmsConfirmAffectedGroup = {
  heading: string;
  items: Array<{ id: string; name?: string; rankingEntityName?: string }>;
  remaining: number;
};

type CmsConfirmDialogProps = {
  affected?: CmsConfirmAffectedGroup[];
  title: string;
  message: string;
  detail?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: CmsConfirmTone;
  returnFocusTo?: RefObject<HTMLElement | null>;
  onConfirm: () => void;
  onCancel: () => void;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function keepTabInside(event: KeyboardEvent, dialog: HTMLElement) {
  const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const active = document.activeElement;
  const outside = !(active instanceof Node) || !dialog.contains(active);
  if (event.shiftKey && (outside || active === first || active === dialog)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (outside || active === last)) {
    event.preventDefault();
    first.focus();
  }
}

function AffectedGroup({ group }: { group: CmsConfirmAffectedGroup }) {
  const headingId = useId();

  return (
    <div className={styles.affectedGroup} role="group" aria-labelledby={headingId}>
      <h4 id={headingId} className={styles.affectedGroupHeading}>
        {group.heading}
      </h4>
      <ul className={styles.affectedList}>
        {group.items.map(item => (
          <li key={item.id} className={styles.affectedItem}>
            <span className={styles.affectedItemName}>{item.name}</span>
            {item.rankingEntityName && (
              <span className={styles.affectedItemEntity}>{item.rankingEntityName}</span>
            )}
          </li>
        ))}
        {group.remaining > 0 && (
          <li className={styles.affectedRemaining}>and {group.remaining} more</li>
        )}
      </ul>
    </div>
  );
}

export function CmsConfirmDialog({
  affected,
  title,
  message,
  detail,
  confirmLabel,
  cancelLabel,
  tone = "warning",
  returnFocusTo,
  onConfirm,
  onCancel,
}: CmsConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
      if (e.key === "Tab" && dialogRef.current) keepTabInside(e, dialogRef.current);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      const target = returnFocusTo?.current ?? previouslyFocused;
      if (target instanceof HTMLElement) target.focus();
    };
  }, [returnFocusTo]);

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        onClick={e => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        tabIndex={-1}
      >
        <div className={styles.iconRow}>
          <div className={`${styles.icon} ${styles[tone]}`}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
        </div>
        <h3 id={titleId} className={styles.title}>
          {title}
        </h3>
        <p id={messageId} className={styles.message}>
          {message}
        </p>
        {detail && <div className={`${styles.detail} ${styles[`${tone}Detail`]}`}>{detail}</div>}
        {affected && affected.length > 0 && (
          <div className={styles.affectedGroups}>
            {affected.map(group => (
              <AffectedGroup key={group.heading} group={group} />
            ))}
          </div>
        )}
        <div className={styles.actions}>
          {cancelLabel && (
            <button type="button" className={styles.cancelButton} onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            className={tone === "danger" ? styles.dangerButton : styles.confirmButton}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
